import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/WhyChooseUsCTA.css';
import QuoteModal from './QuoteModal';

/* Assets */
import indiaFlag from '../assets/india.svg';
import isoIcon from '../assets/iso13485.gif';

const WhyChooseUsCTA = () => {
  const navigate = useNavigate();
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const openQuoteModal = () => {
    setIsQuoteModalOpen(true);
  };

  const closeQuoteModal = () => {
    setIsQuoteModalOpen(false);
  };

  const handleContactSales = () => {
    navigate('/contact');
  };

  const handleDownloadBrochure = () => {
    window.open('/brochure.pdf', '_blank');
  };

  /* ========================================================
     COMPARISON DATA
  ======================================================== */

  const advantages = [
    {
      id: 1,
      title: 'Instant Service & Replacement',
      ourFeature: 'Instant Service & Replacement',
      otherFeature: 'Weeks of waiting time',
      icon: 'fa-solid fa-bolt'
    },
    {
      id: 2,
      title: 'Minimum Order Quantity',
      ourFeature: 'Low MOQ - Start small',
      otherFeature: 'High minimum orders',
      icon: 'fa-solid fa-box-open'
    },
    {
      id: 3,
      title: 'Custom Branding',
      ourFeature: '100% Private Label Available',
      otherFeature: 'Limited/No options',
      icon: 'fa-solid fa-palette'
    },
    {
      id: 4,
      title: 'Pricing',
      ourFeature: 'Affordable, High Margins',
      otherFeature: 'Expensive, Low Margins',
      icon: 'fa-solid fa-indian-rupee-sign'
    }
  ];

  /* ========================================================
     VALUE PROPOSITION DATA
  ======================================================== */

  const valueProps = [
    {
      id: 1,
      iconType: 'flag',
      icon: indiaFlag,
      title: 'Made in India',
      description: 'Faster delivery, better pricing, complete control'
    },
    {
      id: 2,
      iconType: 'certification',
      icon: isoIcon,
      title: 'ISO 13485:2016 Certified',
      description: 'Medical Devices Quality Management System'
    },
    {
      id: 3,
      iconType: 'fontawesome',
      icon: 'fa-solid fa-location-dot',
      title: 'Pan-India Network',
      description: 'Service available across the country'
    }
  ];

  /* ========================================================
     VALUE ICON RENDERER
  ======================================================== */

  const renderValueIcon = (prop) => {
    if (prop.iconType === 'flag') {
      return (
        <img
          src={prop.icon}
          alt="Indian National Flag"
          className="why-choose-us-india-flag"
          loading="lazy"
        />
      );
    }

    if (prop.iconType === 'certification') {
      return (
        <img
          src={prop.icon}
          alt="ISO 13485:2016 Certification"
          className="why-choose-us-certification-logo"
          loading="lazy"
        />
      );
    }

    return (
      <i
        className={prop.icon}
        aria-hidden="true"
      ></i>
    );
  };

  return (
    <>
      <section
        id="why-choose-us"
        className="why-choose-us-section"
      >
        <div className="why-choose-us-container">

          {/* =================================================
              SECTION HEADER
          ================================================= */}

          <div className="why-choose-us-header">

            <span className="why-choose-us-tagline">
              The Clear Choice
            </span>

            <h2 className="why-choose-us-title">
              Why Choose{' '}
              <span className="why-choose-us-highlight">
                Preferred Audiology?
              </span>
            </h2>

            <div className="why-choose-us-underline"></div>

            <p className="why-choose-us-subtitle">
              See how we compare with other manufacturers.
              The difference is clear.
            </p>

          </div>


          {/* =================================================
              COMPARISON TABLE
          ================================================= */}

          <div className="why-choose-us-comparison">

            {/* Comparison Header */}

            <div className="why-choose-us-comparison-header">

              <div className="why-choose-us-comparison-feature">
                Feature
              </div>

              <div className="why-choose-us-comparison-other">
                Other Manufacturers
              </div>

              <div className="why-choose-us-comparison-our">
                Preferred Audiology
              </div>

            </div>


            {/* Comparison Rows */}

            {advantages.map((adv) => (

              <div
                key={adv.id}
                className="why-choose-us-comparison-row"
              >

                {/* Feature */}

                <div className="why-choose-us-comparison-feature">

                  <span className="why-choose-us-comparison-icon">
                    <i
                      className={adv.icon}
                      aria-hidden="true"
                    ></i>
                  </span>

                  <span>
                    {adv.title}
                  </span>

                </div>


                {/* Other Manufacturers */}

                <div className="why-choose-us-comparison-other">

                  <span className="why-choose-us-comparison-minus">
                    <i
                      className="fa-solid fa-xmark"
                      aria-hidden="true"
                    ></i>
                  </span>

                  <span>
                    {adv.otherFeature}
                  </span>

                </div>


                {/* Preferred Audiology */}

                <div className="why-choose-us-comparison-our">

                  <span className="why-choose-us-comparison-plus">
                    <i
                      className="fa-solid fa-check"
                      aria-hidden="true"
                    ></i>
                  </span>

                  <span>
                    {adv.ourFeature}
                  </span>

                </div>

              </div>

            ))}

          </div>


          {/* =================================================
              VALUE PROPOSITION
          ================================================= */}

          <div className="why-choose-us-value">

            <h3 className="why-choose-us-value-title">
              Our Value Proposition
            </h3>

            <div className="why-choose-us-value-grid">

              {valueProps.map((prop) => (

                <div
                  key={prop.id}
                  className="why-choose-us-value-card"
                >

                  {/* Icon / Logo */}

                  <div className="why-choose-us-value-icon">
                    {renderValueIcon(prop)}
                  </div>


                  {/* Content */}

                  <div className="why-choose-us-value-content">

                    <h4>
                      {prop.title}
                    </h4>

                    <p>
                      {prop.description}
                    </p>

                  </div>

                </div>

              ))}

            </div>

          </div>


          {/* =================================================
              CALL TO ACTION
          ================================================= */}

          <div className="why-choose-us-cta">

            <div className="why-choose-us-cta-content">

              {/* CTA Left */}

              <div className="why-choose-us-cta-left">

                <div className="why-choose-us-cta-icon">
                  <i
                    className="fa-solid fa-handshake"
                    aria-hidden="true"
                  ></i>
                </div>

                <div>

                  <h3>
                    Ready to Grow Your Business?
                  </h3>

                  <p>
                    Join India's most innovative hearing aid network
                  </p>

                </div>

              </div>


              {/* CTA Buttons */}

              <div className="why-choose-us-cta-right">

                {/* Become Partner */}

                <button
                  type="button"
                  onClick={openQuoteModal}
                  className="why-choose-us-cta-btn primary"
                >
                  Become a Partner

                  <i
                    className="fa-solid fa-arrow-right why-choose-us-button-icon"
                    aria-hidden="true"
                  ></i>
                </button>


                {/* Contact Sales */}

                <button
                  type="button"
                  onClick={handleContactSales}
                  className="why-choose-us-cta-btn secondary"
                >
                  Contact Sales
                </button>


                {/* Download Brochure */}

                <button
                  type="button"
                  onClick={handleDownloadBrochure}
                  className="why-choose-us-cta-btn download"
                >
                  Download Brochure

                  <i
                    className="fa-solid fa-download why-choose-us-button-icon"
                    aria-hidden="true"
                  ></i>
                </button>

              </div>

            </div>


            {/* CTA Note */}

            <div className="why-choose-us-cta-note">
              <span>
                *Limited partnership slots available per city
              </span>
            </div>

          </div>

        </div>
      </section>


      {/* =====================================================
          QUOTE MODAL
      ===================================================== */}

      <QuoteModal
        isOpen={isQuoteModalOpen}
        onClose={closeQuoteModal}
      />

    </>
  );
};

export default WhyChooseUsCTA;